import { useState, type MouseEvent } from "react";

type Props = {
  idleLabel: string;
  confirmLabel: string;
  onConfirm: () => void;
  /** When true, render the armed (confirm) UI. Used by tests; omit in app (internal state). */
  armed?: boolean;
  /** submit = form submit on confirm; button = click handler only */
  confirmType?: "button" | "submit";
  className?: string;
};

/**
 * Two-click gate for destructive / irreversible platform actions.
 * Idle click only arms; confirm runs the action. Cancel disarms.
 */
export function ConfirmGate({
  idleLabel,
  confirmLabel,
  onConfirm,
  armed: armedProp,
  confirmType = "button",
  className = "ops-btn",
}: Props) {
  const [armedState, setArmed] = useState(false);
  const armed = armedProp ?? armedState;

  if (!armed) {
    return (
      <button
        type="button"
        className={className}
        data-confirm-idle="true"
        onClick={(e: MouseEvent) => {
          e.preventDefault();
          setArmed(true);
        }}
      >
        {idleLabel}
      </button>
    );
  }

  return (
    <div className="platform-confirm-gate" role="group" aria-label={`Confirm ${idleLabel}`}>
      <button type="button" className="ops-btn ops-btn-ghost" onClick={() => setArmed(false)}>
        Cancel
      </button>
      <button
        type={confirmType}
        className={className}
        data-confirm-armed="true"
        onClick={(e: MouseEvent) => {
          setArmed(false);
          if (confirmType === "button") {
            e.preventDefault();
            onConfirm();
          }
          // submit: form onSubmit runs the action
        }}
      >
        {confirmLabel}
      </button>
    </div>
  );
}
